import React from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { showSnackbar } from "../../store/slices/uiSlice";
import StatCard from "../../components/shared/StatCard";
import Avatar from "../../components/shared/Avatar";

const perfStats = [
  {
    label: "Avg Score",
    value: "89.5",
    icon: "Stars",
    delta: "+3.2 vs Q4",
    deltaType: "up" as const,
    color: "#85D1DB",
    iconBg: "rgba(133,209,219,0.1)",
  },
  {
    label: "Reviews Done",
    value: "41",
    icon: "RateReview",
    delta: "83% complete",
    deltaType: "up" as const,
    color: "#16a34a",
    iconBg: "#dcfce7",
  },
  {
    label: "Top Performers",
    value: "12",
    icon: "EmojiEvents",
    delta: "Score ≥ 90",
    deltaType: "neutral" as const,
    color: "#d97706",
    iconBg: "#fef3c7",
  },
  {
    label: "Pending Reviews",
    value: "8",
    icon: "PendingActions",
    delta: "Due Apr 15",
    deltaType: "down" as const,
    color: "#dc2626",
    iconBg: "#fee2e2",
  },
];

const okrs = [
  {
    title: "Grow ARR to ₹50 Cr",
    icon: "📈",
    krs: [
      { label: "Acquire 40 new enterprise clients", pct: 72 },
      { label: "Reduce churn to < 5%", pct: 88 },
      { label: "Expand to 3 new cities", pct: 67 },
    ],
  },
  {
    title: "Improve Product Quality",
    icon: "🛠️",
    krs: [
      { label: "Achieve 99.9% uptime", pct: 97 },
      { label: "Reduce bug backlog by 60%", pct: 54 },
      { label: "Ship 8 major features", pct: 62 },
    ],
  },
  {
    title: "Build World-Class Team",
    icon: "🌟",
    krs: [
      { label: "eNPS score ≥ 60", pct: 78 },
      { label: "Fill 15 open roles", pct: 47 },
      { label: "Complete L&D programs", pct: 74 },
    ],
  },
];

// Using global theme for table headers

const Performance: React.FC = () => {
  const dispatch = useAppDispatch();
  const records = useAppSelector((s) => s.data.performance);
  const RANKS = ["🥇", "🥈", "🥉"];
  const scoreColor = (s: number) =>
    s >= 90 ? "#16a34a" : s >= 75 ? "#d97706" : "#dc2626";

  return (
    <Box>
      <SEO title="Performance" noindex />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {perfStats.map((s) => (
          <Grid item xs={6} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: "14px 18px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                Leaderboard — Q1 2026
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add sx={{ fontSize: 16 }} />}
                onClick={() =>
                  dispatch(
                    showSnackbar({ message: "New review cycle created" }),
                  )
                }
                sx={{ fontSize: 12, borderRadius: 1.5 }}
              >
                New Cycle
              </Button>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 800, '& th, & td': { whiteSpace: 'nowrap' } }}>
                <TableHead>
                  <TableRow>
                    {[
                      "#",
                      "Employee",
                      "Department",
                      "Score",
                      "Rating",
                      "Goals",
                    ].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((r, i) => (
                    <TableRow
                      key={r.id}
                      sx={{
                        transition: "background 0.15s",
                        "&:hover": { background: "#fafbff" },
                      }}
                    >
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 18,
                          textAlign: "center",
                          width: 48,
                        }}
                      >
                        {RANKS[i] || `#${i + 1}`}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            initials={r.initials}
                            color={r.avColor}
                            size={32}
                          />
                          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                            {r.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 12,
                          color: "#64748b",
                        }}
                      >
                        {r.dept}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: 700,
                              fontFamily: "'JetBrains Mono',monospace",
                              color: scoreColor(r.score),
                              width: 36,
                            }}
                          >
                            {r.score}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={r.score}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 99,
                              backgroundColor: "#f1f5f9",
                              minWidth: 60,
                              "& .MuiLinearProgress-bar": {
                                background: scoreColor(r.score),
                                borderRadius: 99,
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 16,
                        }}
                      >
                        {"⭐".repeat(r.rating)}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 13,
                          fontFamily: "'JetBrains Mono',monospace",
                          fontWeight: 600,
                        }}
                      >
                        {r.goals}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: "14px 18px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                Company OKRs — Q2 2026
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add sx={{ fontSize: 16 }} />}
                onClick={() => dispatch(showSnackbar({ message: "OKR added" }))}
                sx={{ fontSize: 12, borderRadius: 1.5 }}
              >
                Add OKR
              </Button>
            </Box>
            <CardContent>
              {okrs.map((o, i) => (
                <Box
                  key={i}
                  sx={{
                    background: "#f8fafc",
                    borderRadius: 2,
                    p: 2,
                    mb: 1.5,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span>{o.icon}</span>
                    {o.title}
                  </Typography>
                  {o.krs.map((kr, j) => (
                    <Box key={j} sx={{ mb: 1.2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "#85D1DB",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{ fontSize: 11.5, color: "#475569", flex: 1 }}
                        >
                          {kr.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontFamily: "'JetBrains Mono',monospace",
                            color: "#94a3b8",
                            minWidth: 32,
                            textAlign: "right",
                          }}
                        >
                          {kr.pct}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={kr.pct}
                        sx={{
                          height: 4,
                          borderRadius: 99,
                          backgroundColor: "#e2e8f0",
                          ml: 1.5,
                          "& .MuiLinearProgress-bar": {
                            background: "#85D1DB",
                            borderRadius: 99,
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Performance;
